/**
 * Return-URL persistence across the full-page reloads that end a session.
 *
 * The in-Redux copy (utils.url) dies with the document: handleUnauthorized
 * captures where the student was and then window.location.assign()s to /login
 * or /verify-device, and the fresh document boots a fresh store with
 * url: undefined — so that capture never survived to the form that needed it,
 * and every session expiry landed on home. The four guest-CTA writers
 * navigate WITHOUT a reload, which is why the Redux copy works for them.
 *
 * sessionStorage is the right scope: it survives same-tab reloads, is per-tab
 * (two tabs' return URLs never fight over one key), and dies with the tab (no
 * stale returns days later).
 *
 * Only same-origin RELATIVE paths are stored, and the check is repeated on
 * read: storage is writable by anything on the origin, and navigating to an
 * absolute URL from here would be an open-redirect gadget.
 */
const RETURN_URL_KEY = 'eduact.return_url'

const isSafeRelativePath = (path: string | null | undefined): path is string =>
	typeof path === 'string' && path.startsWith('/') && !path.startsWith('//')

export const saveReturnUrl = (path: string): void => {
	if (!isSafeRelativePath(path)) return
	try {
		sessionStorage.setItem(RETURN_URL_KEY, path)
	} catch {
		// Storage can be unavailable (privacy modes, quota). Losing the return
		// URL degrades to the old land-on-home behaviour, never to an error.
	}
}

/** Read AND clear — a return URL is single-use by definition. */
export const consumeReturnUrl = (): string | undefined => {
	try {
		const stored = sessionStorage.getItem(RETURN_URL_KEY)
		if (stored !== null) sessionStorage.removeItem(RETURN_URL_KEY)
		return isSafeRelativePath(stored) ? stored : undefined
	} catch {
		return undefined
	}
}
